/**
 * Minimal Facebook JS SDK loader for Meta Embedded Signup. Loads
 * https://connect.facebook.net/en_US/sdk.js once, calls `FB.init()`, and
 * resolves with the global `window.FB` handle.
 *
 * The real Meta-hosted popup (`FB.login`) cannot be meaningfully driven by an
 * automated test - it requires a real browser and a real Meta session.
 * `window.FB` is stubbed at this module's boundary instead
 * (`apps/web/test/channels.test.tsx`), the same way `fetch` is mocked
 * elsewhere in this codebase.
 */

export interface FacebookAuthResponse {
  code?: string;
  accessToken?: string;
}

export interface FacebookLoginResponse {
  authResponse?: FacebookAuthResponse | null;
  status?: string;
}

export interface FacebookLoginOptions {
  config_id: string;
  response_type: 'code';
  override_default_response_type: true;
  extras?: Record<string, unknown>;
}

export interface FacebookSdk {
  init(options: {
    appId: string;
    autoLogAppEvents?: boolean;
    xfbml?: boolean;
    version: string;
  }): void;
  login(callback: (response: FacebookLoginResponse) => void, options: FacebookLoginOptions): void;
}

declare global {
  interface Window {
    FB?: FacebookSdk;
    fbAsyncInit?: () => void;
  }
}

const SDK_VERSION = 'v26.0';
const SDK_SRC = 'https://connect.facebook.net/en_US/sdk.js';
const SDK_SCRIPT_ID = 'facebook-jssdk';

let loadPromise: Promise<FacebookSdk> | null = null;

/**
 * Loads and initializes the Facebook JS SDK with `appId`. Resolves with
 * `window.FB`. Rejects if `appId` is missing/empty - Embedded Signup cannot
 * start without the platform Meta App id (`VITE_WHATSAPP_APP_ID`).
 */
export function loadFacebookSdk(appId: string | undefined): Promise<FacebookSdk> {
  if (!appId) {
    return Promise.reject(
      new Error('WhatsApp guided connect is not configured (missing VITE_WHATSAPP_APP_ID).'),
    );
  }
  if (window.FB) return Promise.resolve(window.FB);
  if (loadPromise) return loadPromise;

  loadPromise = new Promise<FacebookSdk>((resolve, reject) => {
    window.fbAsyncInit = () => {
      if (!window.FB) {
        reject(new Error('The Facebook SDK failed to initialize.'));
        return;
      }
      window.FB.init({ appId, autoLogAppEvents: true, xfbml: false, version: SDK_VERSION });
      resolve(window.FB);
    };

    if (document.getElementById(SDK_SCRIPT_ID)) return; // fbAsyncInit fires once it finishes loading

    const script = document.createElement('script');
    script.id = SDK_SCRIPT_ID;
    script.src = SDK_SRC;
    script.async = true;
    script.defer = true;
    script.crossOrigin = 'anonymous';
    script.onerror = () => reject(new Error('Could not load the Facebook SDK.'));
    document.body.appendChild(script);
  });

  return loadPromise;
}

/** Test-only helper: clears the module-level load cache between tests. */
export function resetFacebookSdkLoadCache(): void {
  loadPromise = null;
}
